import "bulma";
import "./i18n";
import "./styles/styles.styl";

import moment from "moment";
import React from "react";
import ReactDOM from "react-dom";
import { Route, Router, Switch } from "react-router";
import { Link } from "react-router-dom";
import EmbedWordpress from "./components/EmbedWordpress";
import EmbedWordpressMenu from "./components/EmbedWordpressMenu";
import LoginRequiredModal from "./components/LoginRequiredModal";
import RegisterModal from "./components/RegisterModal";
import {
    SERVER_ERROR,
} from "./constants";
import ICustomer from "./interfaces/ICustomer";
import IError from "./interfaces/IError";
import IProduct from "./interfaces/IProduct";
import IProductGroup from "./interfaces/IProductGroup";
import IProductPlayType from "./interfaces/IProductPlayType";
import NotFoundPage from "./pages/NotFoundPage";
import ProductGroupPage from "./pages/ProductGroupPage";
import ProductGroupsPage from "./pages/ProductGroupsPage";
import TicketsPage from "./pages/TicketsPage";
import TransactionsPage from "./pages/TransactionsPage";
import {
    history,
    OGS_LOGO_IMAGE,
    WORDPRESS_BOTTOM_MENU_ID,
    WORDPRESS_TOP_MENU_ID,
    WORDPRESS_URL,
} from "./settings";
import formatOgsDollar from "./utils/formatOgsDollar";
import OGSAPI from "./utils/OGSAPI";
import t from "./utils/t";

const OGSLogo = OGS_LOGO_IMAGE;

interface IState {
    currentYear: number;
    customer: ICustomer | undefined;
    customerMenuVisible: boolean;
    errorLoading: boolean;
    fetchingCustomer: boolean;
    fetchingLogin: boolean;
    loading: boolean;
    loginData: {
        password: string;
        username: string;
    };
    loginError?: string;
    loginRequiredVisible: boolean;
    productGroups: IProductGroup[];
    productPlayTypes: IProductPlayType[];
    products: IProduct[];
    registerVisible: boolean;
}

class Application extends React.Component<{}, IState> {
    public iframeRef: HTMLIFrameElement | undefined;
    public state: IState = {
        currentYear: moment().year(),
        customer: undefined,
        customerMenuVisible: false,
        errorLoading: false,
        fetchingCustomer: true,
        fetchingLogin: false,
        loading: true,
        loginData: {
            password: "",
            username: "",
        },
        loginRequiredVisible: false,
        productGroups: [],
        productPlayTypes: [],
        products: [],
        registerVisible: false,
    };
    public componentDidMount(): void {
        history.listen((): void => {
            this.forceUpdate();
        });

        OGSAPI.fetchCurrentCustomer().then((customer: ICustomer) => {
            this.setState({
                customer,
                fetchingCustomer: false,
            });
        }, (): void => {
            this.setState({
                fetchingCustomer: false,
            });
        });

        // Filter to only return active and non-hidden parts
        const filterOptions = {
            active: 1,
            hidden: 0,
        };
        const pgPromise = OGSAPI.fetchProductGroups({ ...filterOptions })
            .then((productGroups: IProductGroup[]): void => {
                this.setState({ productGroups });
            });

        const pptPromise = OGSAPI.fetchProductPlayTypes({ ...filterOptions })
            .then((productPlayTypes: IProductPlayType[]): void => {
                this.setState({ productPlayTypes });
            });

        const pPromise = OGSAPI.fetchProducts({ ...filterOptions })
            .then((products: IProduct[]): void => {
                this.setState({ products });
            });

        // When all of them load, disable spinner
        Promise.all([
            pptPromise,
            pPromise,
            pgPromise,
        ]).then((): void => {
            this.setState({
                loading: false,
            });
        }, (): void => {
            this.setState({
                errorLoading: true,
                loading: false,
            });
        });

        window.addEventListener("message", this.handleMessage.bind(this), false);
    }
    public componentWillUnmount(): void {
        window.removeEventListener("message", this.handleMessage.bind(this), false);
    }
    public render(): JSX.Element {
        const {
            customer,
            customerMenuVisible,
            errorLoading,
            fetchingCustomer,
            fetchingLogin,
            loading,
            loginData,
            loginError,
            loginRequiredVisible,
            registerVisible,
        } = this.state;

        const canSubmitLogin = loginData.username && loginData.password;

        return (
            <Router history={ history }>
                <div onClick={ this.hideCustomerMenu.bind(this) }>
                    { loginRequiredVisible ? (
                        <LoginRequiredModal
                            onClose={ (): void => {
                                this.setState({
                                    loginRequiredVisible: false,
                                });
                            } }
                        />
                    ) : undefined }
                    { registerVisible ? (
                        <RegisterModal
                            onClose={ this.hideRegister.bind(this) }
                            onRegister={ (newCustomer: ICustomer): void => {
                                this.setState({
                                    customer: newCustomer,
                                    fetchingCustomer: false,
                                    registerVisible: false,
                                });
                                this.refreshIFrame();
                            } }
                        />
                    ) : undefined }
                    <div className="top-bar">
                        <div className="columns">
                            <div className="column is-4 logo-column">
                                <Link to="/">
                                    <img className="logo" src={ OGSLogo } alt="Open Gaming Solutions Inc." />
                                </Link>
                            </div>
                            <div className="column is-4 has-text-centered customer-column">
                                { (fetchingCustomer || fetchingLogin) ? (
                                    <div>Loading..</div>
                                ) : (
                                    customer ? (
                                        <div className="logged-in-content">
                                            <div>
                                                Hello, <strong>{ customer.user.username }</strong>!{" "}
                                                <span
                                                    className="icon is-small"
                                                    onClick={ this.toggleCustomerMenu.bind(this) }
                                                >
                                                    <i className="fa fa-angle-down" aria-hidden="true"></i>
                                                </span>
                                            </div>
                                            { customerMenuVisible ? (
                                                <div
                                                    onClick={ this.hideCustomerMenu.bind(this) }
                                                    className="dropdown has-text-left is-active"
                                                >
                                                    <div className="dropdown-menu" id="dropdown-menu" role="menu">
                                                        <div className="dropdown-content">
                                                            <Link className="dropdown-item" to="/tickets/">
                                                                Tickets
                                                            </Link>
                                                            <Link className="dropdown-item" to="/transactions/">
                                                                Transactions
                                                            </Link>
                                                            <a
                                                                className="dropdown-item"
                                                                onClick={ this.logout.bind(this) }
                                                            >
                                                                Logout
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : undefined }
                                            <div>
                                                Balance: <strong>${ formatOgsDollar(customer.balance) }</strong>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="logged-out-content">
                                            <form
                                                onSubmit={ this.login.bind(this) }
                                            >
                                                <div className="field has-addons">
                                                    <input
                                                        className="input"
                                                        autoComplete="username"
                                                        type="text"
                                                        value={ loginData.username }
                                                        placeholder="Username"
                                                        onChange={ (e: React.ChangeEvent<HTMLInputElement>): void => {
                                                            this.setState({
                                                                loginData: {
                                                                    ...loginData,
                                                                    username: e.target.value,
                                                                },
                                                            });
                                                        } }
                                                    />
                                                    &nbsp;
                                                    <input
                                                        className="input"
                                                        autoComplete="current-password"
                                                        type="password"
                                                        value={ loginData.password }
                                                        placeholder="Password"
                                                        onChange={ (e: React.ChangeEvent<HTMLInputElement>): void => {
                                                            this.setState({
                                                                loginData: {
                                                                    ...loginData,
                                                                    password: e.target.value,
                                                                },
                                                            });
                                                        } }
                                                    />
                                                    &nbsp;
                                                    <button
                                                        className="button is-link"
                                                        type="submit"
                                                        disabled={ !canSubmitLogin }
                                                    >Log In</button>
                                                    &nbsp;
                                                    <button
                                                        type="button"
                                                        className="button"
                                                        onClick={ this.showRegister.bind(this) }
                                                    >Register</button>
                                                </div>
                                                { loginError ? (
                                                    <div className="field help is-danger has-text-right">
                                                        { loginError }
                                                    </div>
                                                ) : undefined }
                                            </form>
                                        </div>
                                    )
                                ) }
                            </div>
                            <div className="column is-4 has-text-centered ogs-header-menu">
                                <div className="menu">
                                    <EmbedWordpressMenu
                                        id={ WORDPRESS_TOP_MENU_ID }
                                        loadingContent={ <div>Loading..</div> }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="content-area">
                        { loading ? (
                            <div className="content-wrapper">
                                Loading..
                            </div>
                        ) : (
                            errorLoading ? (
                                <div className="content-wrapper">
                                    <p>The data server may not be reachable at this time.  Please refresh.</p>
                                </div>
                            ) : (
                                this.renderRoutes()
                            )
                        ) }
                    </div>
                    <div className="bottom-bar">
                        <div className="columns">
                            <div className="column is-4 about-us-column">
                                <h2>About Us</h2>
                                <EmbedWordpress
                                    url={ WORDPRESS_URL }
                                    selector="#text-2 .textwidget p"
                                    useInnerHTML={ true }
                                    loadingContent={ <div>Loading..</div> }
                                />
                            </div>
                            <div className="column is-3">
                                <h2>Contact</h2>
                                <EmbedWordpress
                                    url={ WORDPRESS_URL }
                                    selector="#text-3 .textwidget"
                                    useInnerHTML={ true }
                                    loadingContent={ <div>Loading..</div> }
                                />
                            </div>
                            <div className="column is-4 menu-column">
                                <h2>Menu</h2>
                                <EmbedWordpressMenu
                                    id={ WORDPRESS_BOTTOM_MENU_ID }
                                    renderSubmenus={ true }
                                    loadingContent={ <div>Loading..</div> }
                                />
                            </div>
                        </div>
                    </div>
                    <div className="sub-bottom-bar">
                        <div className="columns">
                            <div className="column is-4 about-us-column">
                                <p>&copy; { moment().year() } Open Gaming Solutions.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </Router>
        );
    }
    protected handleMessage(e: MessageEvent): void {
        if (e.data === "redirectToLogin") {
            this.handleRedirectToLogin();
        } else if (e.data === "purchaseCompleted") {
            OGSAPI.fetchCurrentCustomer().then((customer: ICustomer) => {
                this.setState({
                    customer,
                });
            });
        }
    }
    protected handleRedirectToLogin(): void {
        this.setState({
            loginRequiredVisible: true,
        });
    }
    protected hideCustomerMenu(): void {
        this.setState({ customerMenuVisible: false });
    }
    protected hideRegister(): void {
        this.setState({ registerVisible: false });
    }
    protected login(e: Event): void {
        e.preventDefault();

        const { loginData } = this.state;
        this.setState({
            fetchingLogin: true,
            loginData: {
                ...loginData,
                password: "",
                username: "",
            },
            loginError: undefined,
        });
        OGSAPI.login(loginData).then((customer: ICustomer): void => {
            this.setState({
                customer,
                fetchingLogin: false,
            });
            this.refreshIFrame();
        }, (errors?: IError[]): void => {
            const errorKey = (errors && errors.length) ? errors[0].type : SERVER_ERROR;
            const errorData = (errors && errors.length) ? errors[0].data : {};

            this.setState({
                fetchingLogin: false,
                loginError: t(errorKey, errorData),
            });
        });
    }
    protected logout(): void {
        this.setState({
            fetchingLogin: true,
        });
        OGSAPI.logout().then(() => {
            this.setState({
                customer: undefined,
                fetchingLogin: false,
            });
            this.refreshIFrame();
        });
    }
    protected refreshIFrame(): void {
        if (this.iframeRef) {
            const src = this.iframeRef.src;
            this.iframeRef.src = "";
            this.iframeRef.src = src;
        }
    }
    protected renderMenuItem(url: string, title: string, exact: boolean = true): JSX.Element {
        const classes = [];

        if (
            exact && document.location.pathname === url ||
            !exact && document.location.pathname.startsWith(url)
        ) {
            classes.push("selected");
        }

        return (
            <li className={ classes.join(" ") }><Link to={ url }>{ title }</Link></li>
        );
    }
    protected showCustomerMenu(): void {
        this.setState({ customerMenuVisible: true });
    }
    protected showRegister(): void {
        this.setState({ registerVisible: true });
    }
    protected toggleCustomerMenu(e: React.MouseEvent<HTMLElement>): void {
        e.stopPropagation();
        e.preventDefault();
        this.setState({ customerMenuVisible: !this.state.customerMenuVisible });
    }

    private renderRoutes(): JSX.Element {
        const {
            customer,
            productGroups,
            products,
            productPlayTypes,
        } = this.state;

        const productGroupsByAbbrev: { [key: string]: IProductGroup } = {};
        productGroups.forEach((group: IProductGroup): void => {
            productGroupsByAbbrev[group.abbrev] = group;
        });

        const productsByAbbrev: { [key: string]: IProduct } = {};
        products.forEach((product: IProduct): void => {
            productsByAbbrev[product.abbrev] = product;
        });

        const productPlayTypesByAbbrev: { [key: string]: IProductPlayType } = {};
        productPlayTypes.forEach((ppt: IProductPlayType): void => {
            productPlayTypesByAbbrev[ppt.abbrev] = ppt;
        });

        return (
            <Switch>
                <Route path="/transactions/" render={ (): JSX.Element => {
                    return (
                        <TransactionsPage
                            customer={ customer }
                        />
                    );
                } } />
                <Route path="/tickets/" render={ (): JSX.Element => {
                    return (
                        <TicketsPage
                            customer={ customer }
                        />
                    );
                } } />
                <Route path="/:groupAbbrev/:productAbbrev/" render={ (
                    props: {
                        match: {
                            params: {
                                groupAbbrev: string;
                                productAbbrev: string;
                            };
                        };
                    },
                ): JSX.Element => {
                    const { groupAbbrev, productAbbrev } = props.match.params;

                    // Look up the group entry
                    const group = productGroupsByAbbrev[groupAbbrev];

                    if (!group) {
                        return (
                            <NotFoundPage />
                        );
                    }

                    const ppt = productPlayTypesByAbbrev[productAbbrev];

                    if (!ppt) {
                        return (
                            <NotFoundPage />
                        );
                    }

                    // Extract iframe embedding information
                    const { clientSettings } = ppt.settings;
                    const embed = clientSettings ? clientSettings.embed : undefined;

                    const DEFAULT_WIDTH = "100%";
                    const DEFAULT_HEIGHT = 800;

                    const embedClasses = ["embedded-game"];
                    const embedStyles: { [key: string]: string } = {};

                    if (embed && embed.primaryColor) {
                        embedClasses.push("has-primary-color");
                        embedStyles.borderTopColor = embed.primaryColor;
                    }

                    return (
                        <div>
                            <EmbedWordpress slug={ `productplaytype-${ productAbbrev.toLowerCase() }` } />
                            <div className={ embedClasses.join(" ") } style={ embedStyles }>
                                { embed ? (
                                    <div>
                                        { embed.logo ? (
                                            <img
                                                className={ embed.logo.className }
                                                src={ embed.logo.url }
                                                alt={ embed.logo.alt }
                                            />
                                        ) : undefined }
                                        <iframe
                                            ref={ (ref: HTMLIFrameElement): void => {
                                                this.iframeRef = ref;
                                            } }
                                            src={ embed.url }
                                            width={ embed.width || DEFAULT_WIDTH }
                                            height={ embed.height || DEFAULT_HEIGHT }
                                        ></iframe>
                                    </div>
                                ) : (
                                    <div className="content-wrapper">
                                        Product play type is missing embed settings.
                                    </div>
                                ) }
                            </div>
                        </div>
                    );
                } } />
                <Route path="/:group/" render={
                    (props: {match: { params: { group: string }}}): JSX.Element => {
                        const abbrev = props.match.params.group;

                        const productGroup = productGroups.find((group: IProductGroup): boolean => {
                            return group.abbrev === abbrev;
                        });

                        if (!productGroup) {
                            return (
                                <div className="content-wrapper">
                                    Product group not found!
                                </div>
                            );
                        }

                        const filteredProducts: IProduct[] = products.filter((
                            product: IProduct,
                        ): boolean => {
                            return productGroup.productIds.includes(product.id);
                        });
                        const filteredProductIds: string[] = filteredProducts.map((p: IProduct): string => {
                            return p.id;
                        });

                        const filteredProductPlayTypes = productPlayTypes.filter((
                            ppt: IProductPlayType,
                        ): boolean => {
                            return filteredProductIds.includes(ppt.productId);
                        });

                        const { clientSettings } = productGroup.settings;
                        const displayItemsType =
                            clientSettings ? clientSettings.displayItemsType : undefined;

                        return (
                            <ProductGroupPage
                                productGroup={ productGroup }
                                products={ filteredProducts }
                                productPlayTypes={ filteredProductPlayTypes }
                                displayItemsType={ displayItemsType }
                            />
                        );
                    }
                } />
                <Route path="/" render={ (): JSX.Element => {
                    return (
                        <>
                            <ProductGroupsPage
                                productGroups={ productGroups }
                            />
                        </>
                    );
                } } />
                <Route render={ (): JSX.Element => {
                    return (
                        <div className="content-wrapper">
                            Page not found
                        </div>
                    );
                } } />
            </Switch>
        );
    }
}

ReactDOM.render(
    <Application />,
    document.getElementById("application"),
);



// WEBPACK FOOTER //
// ./src/application.tsx